import {
  PLAYER_PLATFORM_LABELS,
  PLAYER_PLATFORMS,
  playerPlatformSchema,
  TEAM_AVAILABILITY_DAY_LABELS,
  TEAM_AVAILABILITY_DAYS,
  TEAM_AVAILABILITY_PERIOD_LABELS,
  TEAM_AVAILABILITY_PERIODS,
  teamPlayerSheetSchema,
  teamPlayerSheetsSchema,
  type AdminTeam,
  type PlayerGameAccount,
  type TeamGame,
  type TeamPlayerSheet,
  type TeamPlayerSheetInput,
} from '@growup/site-shared';
import { Button, Card, Checkbox, Field, Input, Notice, Select, Textarea } from '@growup/ui';
import { useEffect, useState } from 'react';

import { getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';

const BASE = '/api/site/teams';

type Slot = TeamPlayerSheetInput['availability'][number];

/**
 * Ficha interna de cada jogador (parte 8, docs/equipas, RF-EQ18): contas de
 * jogo, rank, posicao, disponibilidade e notas do treinador. So o Gestor de
 * Equipas e o Manager da equipa a veem.
 */
export function SheetsCard({ team, game }: { team: AdminTeam; game: TeamGame | undefined }) {
  const [players, setPlayers] = useState<TeamPlayerSheet[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getJson(`${BASE}/${team.id}/sheets`, teamPlayerSheetsSchema)
      .then((s) => setPlayers(s.players))
      .catch((e: unknown) => setError(messageOf(e)));
  }, [team.id]);

  if (players === null) {
    return (
      <Card headingLevel={2} title="Ficha interna">
        {error ? <Notice tone="danger">{error}</Notice> : <p>A carregar…</p>}
      </Card>
    );
  }

  const replace = (next: TeamPlayerSheet) =>
    setPlayers((list) => list?.map((p) => (p.accountId === next.accountId ? next : p)) ?? null);
  const current = players.filter((p) => p.current);
  const former = players.filter((p) => !p.current);
  const row = (p: TeamPlayerSheet) => (
    <li key={p.accountId}>
      <PlayerRow
        team={team}
        game={game}
        player={p}
        open={open === p.accountId}
        onToggle={() => setOpen((o) => (o === p.accountId ? null : p.accountId))}
        onSaved={replace}
      />
    </li>
  );

  return (
    <Card headingLevel={2} title="Ficha interna">
      <div className="flex flex-col gap-4">
        <p className="text-gu-ink-muted">
          Só o Gestor de Equipas e o Manager desta equipa veem estas fichas. Nada aparece em público
          nem ao próprio jogador.
        </p>
        {current.length === 0 ? <p>Ainda ninguém no lineup.</p> : null}
        <ul className="flex flex-col">{current.map(row)}</ul>
        {former.length > 0 ? (
          <div className="flex flex-col gap-1">
            <h3 className="font-sub text-label font-bold uppercase text-gu-ink-muted">
              Quem já saiu
            </h3>
            <ul className="flex flex-col">{former.map(row)}</ul>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function summary(p: TeamPlayerSheet, game: TeamGame | undefined) {
  const role = game?.roles.find((r) => r.id === p.gameRoleId)?.name;
  const parts = [
    p.rank,
    role,
    p.gameAccounts.length > 0
      ? p.gameAccounts.map((a) => `${PLAYER_PLATFORM_LABELS[a.platform]} ${a.handle}`).join(', ')
      : null,
    p.notes.length > 0
      ? `${String(p.notes.length)} ${p.notes.length === 1 ? 'nota' : 'notas'}`
      : null,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : 'Ficha por preencher';
}

function PlayerRow({
  team,
  game,
  player,
  open,
  onToggle,
  onSaved,
}: {
  team: AdminTeam;
  game: TeamGame | undefined;
  player: TeamPlayerSheet;
  open: boolean;
  onToggle: () => void;
  onSaved: (p: TeamPlayerSheet) => void;
}) {
  return (
    <div className="flex flex-col gap-3 border-b border-gu-line py-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-semibold">
            {player.login}
            {player.youth ? (
              <span className="ml-2 text-caption font-normal text-gu-ink-muted">jovem</span>
            ) : null}
          </span>
          <span className="text-caption text-gu-ink-muted">{summary(player, game)}</span>
        </div>
        <Button
          size="sm"
          variant="secondary"
          aria-expanded={open}
          aria-label={`${open ? 'Fechar' : 'Abrir'} a ficha de ${player.login}`}
          onClick={onToggle}
        >
          {open ? 'Fechar' : 'Abrir'}
        </Button>
      </div>
      {open ? (
        <div className="flex flex-col gap-5">
          <SheetForm team={team} game={game} player={player} onSaved={onSaved} />
          <Notes team={team} player={player} onSaved={onSaved} />
        </div>
      ) : null}
    </div>
  );
}

function SheetForm({
  team,
  game,
  player,
  onSaved,
}: {
  team: AdminTeam;
  game: TeamGame | undefined;
  player: TeamPlayerSheet;
  onSaved: (p: TeamPlayerSheet) => void;
}) {
  const [rank, setRank] = useState(player.rank);
  const [gameRoleId, setGameRoleId] = useState(player.gameRoleId);
  const [availability, setAvailability] = useState<Set<Slot>>(new Set(player.availability));
  const [accounts, setAccounts] = useState<PlayerGameAccount[]>(player.gameAccounts);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const gameRoles = game?.roles ?? [];
  const unused = PLAYER_PLATFORMS.filter((p) => !accounts.some((a) => a.platform === p));

  const save = async () => {
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      const body: TeamPlayerSheetInput = {
        rank,
        gameRoleId,
        availability: [...availability],
        gameAccounts: accounts.filter((a) => a.handle.trim() !== ''),
      };
      const saved = teamPlayerSheetSchema.parse(
        await send('PUT', `${BASE}/${team.id}/sheets/${player.accountId}`, body),
      );
      onSaved(saved);
      setAccounts(saved.gameAccounts);
      setDone(true);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (slot: Slot) =>
    setAvailability((set) => {
      const next = new Set(set);
      if (next.has(slot)) next.delete(slot);
      else next.add(slot);
      return next;
    });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Rank" optional>
          <Input value={rank} maxLength={60} onChange={(e) => setRank(e.target.value)} />
        </Field>
        <Field label="Posição preferida" optional>
          <Select
            value={gameRoleId ?? ''}
            disabled={gameRoles.length === 0}
            onChange={(e) => setGameRoleId(e.target.value || null)}
          >
            <option value="">{gameRoles.length === 0 ? 'Este jogo não tem' : '—'}</option>
            {gameRoles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-2 font-sub text-label font-bold uppercase text-gu-ink-muted">
          Contas de jogo
        </legend>
        <p className="text-caption text-gu-ink-muted">
          São da pessoa: aparecem nas fichas de todas as equipas onde joga.
        </p>
        {accounts.map((a, i) => (
          <div key={a.platform} className="flex flex-wrap items-end gap-2">
            <Field label="Plataforma" className="w-full sm:w-44">
              <Select
                value={a.platform}
                onChange={(e) => {
                  const platform = playerPlatformSchema.parse(e.target.value);
                  setAccounts((list) => list.map((x, j) => (j === i ? { ...x, platform } : x)));
                }}
              >
                {[a.platform, ...unused].map((p) => (
                  <option key={p} value={p}>
                    {PLAYER_PLATFORM_LABELS[p]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="ID" className="min-w-0 flex-1 basis-40">
              <Input
                value={a.handle}
                maxLength={80}
                onChange={(e) =>
                  setAccounts((list) =>
                    list.map((x, j) => (j === i ? { ...x, handle: e.target.value } : x)),
                  )
                }
              />
            </Field>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              aria-label={`Tirar ${PLAYER_PLATFORM_LABELS[a.platform]}`}
              onClick={() => setAccounts((list) => list.filter((_, j) => j !== i))}
            >
              Tirar
            </Button>
          </div>
        ))}
        {unused[0] ? (
          <div>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() =>
                setAccounts((list) => [...list, { platform: unused[0] ?? 'other', handle: '' }])
              }
            >
              Juntar conta
            </Button>
          </div>
        ) : null}
      </fieldset>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-2 font-sub text-label font-bold uppercase text-gu-ink-muted">
          Disponibilidade semanal
        </legend>
        <div className="overflow-x-auto">
          <table className="text-caption">
            <thead>
              <tr>
                <th />
                {TEAM_AVAILABILITY_DAYS.map((d) => (
                  <th key={d} scope="col" className="px-1 pb-1 font-semibold sm:px-2">
                    {TEAM_AVAILABILITY_DAY_LABELS[d]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TEAM_AVAILABILITY_PERIODS.map((p) => (
                <tr key={p}>
                  <th scope="row" className="pr-2 text-left font-semibold sm:pr-3">
                    {TEAM_AVAILABILITY_PERIOD_LABELS[p]}
                  </th>
                  {TEAM_AVAILABILITY_DAYS.map((d) => {
                    const slot: Slot = `${d}-${p}`;
                    return (
                      <td key={d} className="px-1 py-1 text-center sm:px-2">
                        <Checkbox
                          label={
                            <span className="sr-only">
                              {TEAM_AVAILABILITY_DAY_LABELS[d]} {TEAM_AVAILABILITY_PERIOD_LABELS[p]}
                            </span>
                          }
                          className="justify-center gap-0"
                          checked={availability.has(slot)}
                          onChange={() => toggle(slot)}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </fieldset>

      {error ? <Notice tone="danger">{error}</Notice> : null}
      {done ? <Notice tone="success">Ficha guardada.</Notice> : null}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" disabled={busy}>
          Guardar ficha
        </Button>
        {player.updatedAt ? (
          <span className="text-caption text-gu-ink-muted">
            Última alteração a {formatDateTime(player.updatedAt)} por {player.updatedBy}.
          </span>
        ) : null}
      </div>
    </form>
  );
}

function Notes({
  team,
  player,
  onSaved,
}: {
  team: AdminTeam;
  player: TeamPlayerSheet;
  onSaved: (p: TeamPlayerSheet) => void;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const path = `${BASE}/${team.id}/sheets/${player.accountId}/notes`;

  const run = async (method: string, url: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      onSaved(teamPlayerSheetSchema.parse(await send(method, url, body)));
      return true;
    } catch (e) {
      setError(messageOf(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <h3 className="font-sub text-label font-bold uppercase text-gu-ink-muted">
        Notas do treinador
      </h3>
      <form
        className="flex flex-col gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim() === '') return;
          void run('POST', path, { text }).then((ok) => ok && setText(''));
        }}
      >
        <Field
          label="Nota nova"
          hint="Ficam guardadas cifradas. Só quem escreveu uma nota a pode apagar."
        >
          <Textarea
            rows={3}
            maxLength={2000}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </Field>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <div>
          <Button type="submit" size="sm" variant="secondary" disabled={busy || text.trim() === ''}>
            Juntar nota
          </Button>
        </div>
      </form>
      {player.notes.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {player.notes.map((n) => (
            <li key={n.id} className="border-l-2 border-gu-line pl-3">
              <p className="whitespace-pre-line">{n.text}</p>
              <div className="flex flex-wrap items-center gap-2 text-caption text-gu-ink-muted">
                {formatDateTime(n.createdAt)} · {n.createdBy}
                {n.mine ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => {
                      if (window.confirm('Apagar esta nota?'))
                        void run('DELETE', `${path}/${n.id}`);
                    }}
                  >
                    Apagar
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
